import {
  ChatAttachment,
  ChatAttachmentDTO,
  ChatData,
  ChatDTO,
  ChatMessage,
  ChatMessageDTO,
  ChatMetaDTO,
  ChatParticipant,
  ChatProduct,
  Chats,
  compactMap,
  formatCurrency,
  getUserShortName,
  globalChatsStore,
  WebSocketConnectionUser,
} from 'chats'
import {extractFileName} from '../connect-chat/string.utils'

const extractMessageFromChatDTO = (
  chatDTO: ChatDTO,
): ChatMessageDTO | undefined => {
  const lm = chatDTO.last_message ?? chatDTO.messages
  let lastMessage: ChatMessageDTO | undefined
  if (Array.isArray(lm)) {
    lastMessage = lm[0]
  } else {
    lastMessage = lm
  }
  return lastMessage
}

export const mapSocketChatsToApp = (
  data: ChatDTO[] | undefined,
  currentUser: WebSocketConnectionUser,
): Array<ChatData> => {
  if (!data) return []
  return compactMap(data, (d) => mapChat(d, currentUser))
}

export const mapChat = (
  chatDTO: ChatDTO,
  currentUser: WebSocketConnectionUser,
): ChatData => {
  const recipient: ChatParticipant = extractRecipientFromMetadata(chatDTO)
  const owner: ChatParticipant =
    globalChatsStore.currentParticipant as ChatParticipant
  const lastMessage = extractMessageFromChatDTO(chatDTO)
  const avatar = mapAvatar(lastMessage, currentUser.hash, owner, recipient)

  const mappedMessage = lastMessage ? mapChatMessage(lastMessage) : undefined

  console.log('💬 raw message (last)', lastMessage)
  console.log('💬 mapped message (last)', mappedMessage)
  return {
    id: chatDTO.id,
    createdAt: chatDTO.date_last_active,
    newMessagesCount: chatDTO.unread_messages,
    avatar,
    interlocutor: recipient,
    title: recipient.name,
    lastMessage: mappedMessage,
    product: extractProductFromChatDTO(chatDTO),
    messages: chatDTO.messages
      ? chatDTO.messages.map((m) => mapChatMessage(m))
      : [],
    isTranslatable: false,
    // isTranslatable: chatDTO.user.lang_code !== currentUser.langCode,
  }
}

const mapChatAttachment = (
  attachment?: ChatAttachmentDTO,
): ChatAttachment | undefined => {
  if (!attachment) return
  const fileName = attachment.file_name
    ? attachment.file_name
    : extractFileName(attachment.url)
  return {
    url: attachment.url,
    fileName,
    fileSize: attachment.file_size,
    error: attachment.error,
  }
}

export const mapChatMessage = (message: ChatMessageDTO): ChatMessage => {
  const {type} = message

  let text = message.text
  let location: {lat: number; lng: number} | undefined
  switch (type) {
    case 'location':
      const lat = parseFloat(message.latitude)
      const lng = parseFloat(message.longitude)
      text = Chats.api.google.getGoogleStaticMapLink({
        location: {latitude: lat, longitude: lng},
      })
      location = {lat, lng}
      break
    case 'text':
      text = text?.trim()
      break
  }

  return {
    chatId: message.chat_id,
    id: message.id,
    localId: message.id,
    type,
    text: text,
    location,
    ownerId: message.owner?.hash,
    userId: message.user?.hash,
    ownerName: message.owner?.name ?? '',
    ownerInitials: getUserShortName(message.owner?.name),
    date: message.created_at * 1000,
    image: mapChatAttachment(message.image),
    video: mapChatAttachment(message.video),
    file: mapChatAttachment(message.file),
    isRead: message.is_read === 1,
    isDelivered: message.is_delivered === 1,
  }
}

const extractRecipientFromMetadata = (
  metaData: ChatMetaDTO,
): ChatParticipant => ({
  id: metaData.user.hash,
  name: metaData.user.name,
  avatarSrc: metaData.user.image?.url,
  onlineLastTime: metaData.user.online_lasttime,
  online: metaData.user.online,
  langCode: metaData.user.lang_code,
})

const mapAvatar = (
  message: ChatMessageDTO | undefined,
  currentUserId: string,
  owner: ChatParticipant,
  recipient: ChatParticipant,
): string | undefined => {
  let avatar = message?.image?.url
  if (!avatar || avatar === '')
    avatar = currentUserId === owner.id ? recipient.avatarSrc : owner.avatarSrc
  return avatar
}

const extractProductFromChatDTO = (
  chatDTO: ChatDTO,
): ChatProduct | undefined => {
  if (chatDTO.product.id === 'support') return undefined
  return {
    id: chatDTO.product.hash,
    title: chatDTO.product.name,
    image: chatDTO.product.image,
    price: formatCurrency({
      price: chatDTO.product.price,
      symbol: chatDTO.product.currency,
    }),
    status: chatDTO.product.status,
  }
}
